import { getSession } from '@/services/auth';

// Keys as returned by POST /api/v1/auth/login in the `permissions` array.
export const PERMISSIONS = {
  DASHBOARD_VIEW: 'dashboard.view',
  ACTIVITIES_VIEW: 'activities.view',
  GROUPS_VIEW: 'groups.view',
  USERS_VIEW: 'users.view',
  USERS_MANAGE: 'users.manage',
  WAVES_VIEW: 'waves.view',
  WAVES_MANAGE: 'waves.manage',
  NOTIFICATIONS_VIEW: 'notifications.view',
  NOTIFICATIONS_MANAGE: 'notifications.manage',
  TEMPLATES_VIEW: 'templates.view',
  TEMPLATES_MANAGE: 'templates.manage',
  SCHEDULER_VIEW: 'scheduler.view',
  SCHEDULER_MANAGE: 'scheduler.manage',
  SCHEDULED_JOBS_VIEW: 'scheduled_jobs.view',
  SCHEDULED_JOBS_MANAGE: 'scheduled_jobs.manage',
  ROLES_VIEW: 'roles.view',
  ROLES_MANAGE: 'roles.manage',
  REPORTS_VIEW: 'reports.view',
} as const;

// Sessions created before permissions shipped won't have this field at all —
// treat that as "unknown" (unrestricted) rather than "no access", since it
// just means the user hasn't logged in again yet. An explicit empty array
// means the backend granted zero permissions and should be respected.
export function hasAnyPermission(required: string[]): boolean {
  if (required.length === 0) return true;
  const session = getSession();
  if (!session) return false;
  if (session.is_super_admin) return true;
  if (!session.permissions) return true;
  return required.some((p) => session.permissions!.includes(p));
}

export function hasPermission(permission: string): boolean {
  return hasAnyPermission([permission]);
}

// Some areas (e.g. creating organizations) are restricted to super admins
// regardless of granular permissions.
export function isSuperAdmin(): boolean {
  return getSession()?.is_super_admin === true;
}

// The organization area is shared: super admins see every org, while org
// owners/admins and PICs are scoped to their own by the backend. Both an org
// admin and a group PIC are granted the `org_admin` platform role, which is
// what lets them log into the dashboard at all.
export function isOrgManager(): boolean {
  const session = getSession();
  if (!session) return false;
  return session.is_super_admin === true || session.role === 'org_admin';
}
